'use client';

import { useState } from 'react';
import { HeartHandshake, Sparkles, SquarePen } from 'lucide-react';

const STEPS = [
  {
    icon: SquarePen,
    title: 'Register Free (2 min)',
    body: 'Photo, preferences and privacy settings.',
  },
  {
    icon: Sparkles,
    title: 'Get Daily Matches',
    body: 'Fresh profiles matched to you, every day.',
  },
  {
    icon: HeartHandshake,
    title: 'Connect & Meet',
    body: 'Send interest, chat, and meet with both families.',
  },
] as const;

type Step = (typeof STEPS)[number];

function StepTip({ step, arrowClassName }: { step: Step; arrowClassName: string }) {
  const Icon = step.icon;

  return (
    <div className="relative">
      <span
        aria-hidden
        className={`absolute top-0 h-0 w-0 -translate-x-1/2 -translate-y-full border-x-[10px] border-b-[10px] border-x-transparent border-b-[#f4eefe] ${arrowClassName}`}
      />
      <div className="flex items-start gap-[17px] rounded-[18px] bg-[#f4eefe] p-[21px] text-left shadow-[0_10px_28px_rgba(80,40,140,0.08)]">
        <Icon className="h-7 w-7 shrink-0 text-black" strokeWidth={1.75} aria-hidden />
        <p className="min-h-[40px] text-[14px] leading-5 text-neutral-600">{step.body}</p>
      </div>
    </div>
  );
}

function StepBadge({ index, active }: { index: number; active: boolean }) {
  return (
    <span
      className={`relative z-10 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-[16px] font-semibold ${
        active ? 'bg-primary-600 text-white' : 'bg-[#efe6ff] text-[#6B39FF]'
      }`}
    >
      {index + 1}
    </span>
  );
}

export default function HomeHowItWorks() {
  const [selected, setSelected] = useState(0);

  return (
    <section
      className="bg-white bg-repeat font-sans"
      style={{ backgroundImage: "url('/assets/img/moroccan-bg.png')" }}
    >
      <div
        aria-hidden
        className="h-[100px] w-full bg-[length:auto_100px] bg-repeat-x bg-top"
        style={{ backgroundImage: "url('/assets/img/flowers-rope.png')" }}
      />

      <div className="mx-auto w-full max-w-[1120px] px-5 pb-16 pt-6 md:px-8 md:pb-20 md:pt-8">
        <div className="text-center">
          <p className="text-[13px] font-semibold uppercase tracking-[2px] text-black">How it works</p>
          <h2 className="mx-auto mt-2 max-w-[16ch] text-[28px] font-bold leading-tight text-black md:max-w-none md:text-[40px]">
            Three steps to your perfect match
          </h2>
          <p className="mt-3 text-[15px] text-neutral-600 md:text-[16px]">Tap a step to see how it works</p>
        </div>

        <ol className="mx-auto mt-[50px] flex max-w-[360px] flex-col gap-6 md:hidden">
          {STEPS.map((item, index) => {
            const active = index === selected;
            return (
              <li key={item.title}>
                <button
                  type="button"
                  onClick={() => setSelected(index)}
                  aria-pressed={active}
                  className="flex w-full items-center gap-4 text-left"
                >
                  <StepBadge index={index} active={active} />
                  <span className="text-[16px] font-semibold leading-snug text-black">{item.title}</span>
                </button>
                {active && (
                  <div className="mt-4">
                    <StepTip step={item} arrowClassName="left-[22px]" />
                  </div>
                )}
              </li>
            );
          })}
        </ol>

        <div className="relative mx-auto mt-[50px] hidden md:block">
          <div className="absolute left-[16.666%] right-[16.666%] top-[22px] h-px bg-[#eadfff]" />
          <div className="relative grid grid-cols-3 items-start gap-6">
            {STEPS.map((item, index) => {
              const active = index === selected;
              return (
                <div key={item.title} className="flex flex-col items-center">
                  <button
                    type="button"
                    onClick={() => setSelected(index)}
                    aria-pressed={active}
                    className="flex flex-col items-center text-center"
                  >
                    <StepBadge index={index} active={active} />
                    <span className="mt-3 text-[16px] font-semibold leading-snug text-black md:text-[18px]">
                      {item.title}
                    </span>
                  </button>
                  {active && (
                    <div className="mt-5 w-full max-w-[280px]">
                      <StepTip step={item} arrowClassName="left-1/2" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
